import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, CalendarCheck, AlertTriangle, UserPlus, Lock, CheckCircle2 } from 'lucide-react';
export function LockLineupPage() {
  const steps = [{
    title: 'Define required roles',
    desc: 'Tell Booker which players are required for the act.',
    icon: Users
  }, {
    title: 'Check availability automatically',
    desc: 'Use calendars, blackout dates, and confirmations to verify coverage.',
    icon: CalendarCheck
  }, {
    title: 'Resolve conflicts early',
    desc: 'Flag overlapping commitments before the deal is finalized.',
    icon: AlertTriangle
  }, {
    title: 'Use subs or alternate dates if needed',
    desc: 'Keep good opportunities alive without false confirmation.',
    icon: UserPlus
  }, {
    title: 'Lock the show only when both sides are ready',
    desc: 'Venue accepted, lineup confirmed, contract handled, deposit on track.',
    icon: Lock
  }];
  return <div className="min-h-screen pt-32 pb-24" data-id="element-7229">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 mb-20 text-center" data-id="element-7230">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-4xl md:text-5xl font-bold text-textMain mb-6 tracking-tight" data-id="element-7231">
          A venue can say yes before the band actually can.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-lg text-textMuted leading-relaxed" data-id="element-7232">
          Booker checks member availability, tracks conflicts, requests
          confirmations, and keeps a show from being treated as locked until the
          lineup is truly covered.
        </motion.p>
      </section>

      {/* Problem Section */}
      <section className="max-w-4xl mx-auto px-6 mb-20" data-id="element-7233">
        <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-8 md:p-12 text-center" data-id="element-7234">
          <h2 className="text-2xl font-bold text-textMain mb-4" data-id="element-7235">
            Booking the venue is only half the job.
          </h2>
          <p className="text-textMuted leading-relaxed" data-id="element-7236">
            For many bands, a good offer still is not a real booking until the
            right players are available, confirmed, and aligned with the route.
          </p>
        </div>
      </section>

      {/* Scenario Story */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7237">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 relative overflow-hidden" data-id="element-7238">
          <div className="absolute top-0 left-0 w-1 h-full bg-primary" data-id="element-7239"></div>
          <div className="prose prose-invert max-w-none" data-id="element-7240">
            <p className="italic text-lg text-textMuted leading-relaxed mb-6" data-id="element-7241">
              Mark gets a solid offer for a June date in New England. The venue
              is interested, the money works, and the route makes sense — but
              three other members still need to confirm, and two of them play in
              multiple bands.
            </p>
            <p className="italic text-lg text-textMuted leading-relaxed" data-id="element-7242">
              Booker does not treat the show as fully locked until the right
              people can actually play it. It checks availability, requests
              confirmations, flags conflicts, and suggests alternate dates or
              approved subs when needed.
            </p>
          </div>
        </div>
      </section>

      {/* Workflow Steps */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7243">
        <div className="space-y-6" data-id="element-7244">
          {steps.map((step, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex items-start gap-6 bg-surface border border-border rounded-2xl p-6" data-id="element-7245">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0" data-id="element-7246">
                <step.icon className="w-6 h-6 text-primary" data-id="element-7247" />
              </div>
              <div data-id="element-7248">
                <h3 className="text-xl font-bold text-textMain mb-2" data-id="element-7249">
                  Step {i + 1} — {step.title}
                </h3>
                <p className="text-textMuted" data-id="element-7250">{step.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Outcomes */}
      <section className="max-w-4xl mx-auto px-6 mb-24" data-id="element-7251">
        <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 text-center" data-id="element-7252">
          <h2 className="text-2xl font-bold text-textMain" data-id="element-7253">
            Stop calling a show booked when it still depends on three unanswered
            texts.
          </h2>
        </div>
      </section>

      {/* CTA Band */}
      <section className="max-w-4xl mx-auto px-6 text-center" data-id="element-7254">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-7255">
          <Link to="/onboarding" className="px-8 py-3 bg-primary text-background font-bold rounded-lg hover:bg-primaryHover transition-colors" data-id="element-7256">
            Start free
          </Link>
          <Link to="/demo" className="px-8 py-3 bg-surface border border-border text-textMain font-medium rounded-lg hover:bg-surfaceHover transition-colors" data-id="element-7257">
            See Booker in action
          </Link>
        </div>
      </section>
    </div>;
}